import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  GridLegacy as Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import api from '../services/api';

// Types
interface AuditRecord {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  salesRep?: string;
  attributionId?: string;
  buyerDate?: string;
  saleAmount?: string | number;
  coupon?: string;
  product?: string;
  discount?: string;
  reason?: string;
  rowIndex?: number;
  correctEmail?: string;
  incorrectEmail?: string;
}

interface SheetUpdate {
  rowsUpdated: number;
  cellsUpdated: number;
}

interface AuditSummary {
  totalAttributionRecords: number;
  foundInBoth: number;
  missingFromSalesTracker: number;
  needsEmailUpdate?: number;
}

interface DateRange {
  from: string;
  to: string;
}

interface AuditResults {
  summary: AuditSummary;
  foundInBoth: AuditRecord[];
  missingFromSalesTracker: AuditRecord[];
  needsEmailUpdate?: AuditRecord[];
  missingWithValidCoupon?: AuditRecord[];
  missingWithInvalidCoupon?: AuditRecord[];
  dateRange: DateRange;
  auditDate: string;
  sheetUpdate?: SheetUpdate;
}

interface FormData {
  date: string;
  customerName: string;
  contactNumber: string;
  email: string;
  product: string;
  discount: string;
}

const SalesAuditTool: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [auditResults, setAuditResults] = useState<AuditResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>('');
  const [formSuccess, setFormSuccess] = useState<string>('');
  const [selectedRecord, setSelectedRecord] = useState<AuditRecord | null>(null);
  const [automationSummary, setAutomationSummary] = useState<any>(null);

  // Product and discount options (same as MySalesTracker)
  const products: string[] = [
    'Semaglutide injections',
    'Tirzep Injections',
    'Semaglutide sublingual',
    'Tirzep sublingual',
    'GLP-1 Discount 1 Month',
    'NAD+ Discount 1 Month'
  ];

  const discounts: string[] = [
    'T3_TSLTD118 - LT ($179/$299) 12M',
    'T1_TSLTD100- LT ($197/$299)',
    'T2_TSOTD200- One Time ($97/$149)',
    'T3_CSLTD050- $199 sublingual sema',
    'T3_CSLTD50- $249 sublingual Tirzep'
  ];

  // Form state
  const [formData, setFormData] = useState<FormData>({
    date: '',
    customerName: '',
    contactNumber: '',
    email: '',
    product: '',
    discount: ''
  });

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    setAuditResults(null);
    setAutomationSummary(null);

    try {
      const response = await api.runSalesAudit();

      console.log('Audit completed successfully');
      if (response.sheetUpdate) {
        console.log(`✓ Sheet updated: ${response.sheetUpdate.rowsUpdated} rows`);
      }
      
      setAuditResults(response);
    } catch (err: any) {
      console.error('Audit error:', err);
      setError(err.response?.data?.error || 'Failed to run audit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const runAutomatedAudit = async () => {
    setLoading(true);
    setError(null);
    setAuditResults(null);
    setAutomationSummary(null);

    try {
      const response = await api.runAutomatedAudit();

      console.log('Automated audit completed successfully');
      if (response.auditResults) {
        setAuditResults(response.auditResults);
      }
      if (response.automationSummary) {
        setAutomationSummary(response.automationSummary);
      }
    } catch (err: any) {
      console.error('Automated audit error:', err);
      setError(err.response?.data?.error || 'Failed to run automated audit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch (e) {
      return dateStr;
    }
  };

  const formatCurrency = (amount?: string | number): string => {
    if (!amount) return 'N/A';
    const numAmount = parseFloat(String(amount));
    if (isNaN(numAmount)) return String(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numAmount);
  };

  const handleAddToSalesTracker = (record: AuditRecord) => {
    // Get yesterday's date
    const yesterday = subDays(new Date(), 1);
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd');

    // Pre-populate form with record data
    const fullName = `${record.firstName || ''} ${record.lastName || ''}`.trim();
    
    setSelectedRecord(record);
    setFormData({
      date: yesterdayStr,
      customerName: fullName || '',
      contactNumber: record.phone || '',
      email: record.email || '',
      product: '',
      discount: ''
    });
    setFormError('');
    setFormSuccess('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormError('');
    setFormSuccess('');
    setSelectedRecord(null);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.date || !formData.customerName || !formData.contactNumber || 
        !formData.email || !formData.product || !formData.discount) {
      setFormError('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    try {
      setSubmitting(true);
      setFormError('');
      
      // Include attributionId from selected record so backend can look up the correct sales_tracker_id
      const submitData = {
        ...formData,
        attributionId: selectedRecord?.attributionId || selectedRecord?.salesRep
      };
      
      console.log('Submitting sale with attribution ID:', submitData.attributionId);
      
      await api.createMySale(submitData);
      
      setFormSuccess('Sale added to Sales Tracker successfully!');
      
      // Close dialog after 1.5 seconds and refresh audit
      setTimeout(() => {
        handleDialogClose();
        runAudit(); // Re-run audit to update results
      }, 1500);

    } catch (err: any) {
      console.error('Error adding sale:', err);
      setFormError(err.response?.data?.error || 'Failed to add sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEmail = async (record: AuditRecord) => {
    if (!record.rowIndex || !record.correctEmail) return;
    
    try {
      await api.updateSalesEmail(record.rowIndex, record.correctEmail);
      // Re-run audit to refresh results
      runAudit();
    } catch (err) {
      console.error('Error updating email:', err);
      setError('Failed to update email');
    }
  };

  const handleBulkAdd = async (sales: AuditRecord[]) => {
    try {
      setLoading(true);
      await api.bulkAddSales(sales);
      // Re-run audit to refresh results
      await runAudit();
    } catch (err) {
      console.error('Error bulk adding sales:', err);
      setError('Failed to bulk add sales');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Sales Audit Tool
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Compare sales in the Attribution Sheet with the Sales Tracker for all Direct Meds users
          (active, inactive, all user types, all attribution tags - 2026 sales only)
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Automated Process:</strong> When you click "Run Audit", the system will:
          </Typography>
          <Typography variant="body2" component="ol" sx={{ ml: 2, mt: 1 }}>
            <li>First, automatically update the attribution data from the source sheet</li>
            <li>Then, run the audit comparison with the latest data</li>
          </Typography>
        </Alert>
      </Box>

      {/* Run Audit Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
          onClick={runAudit}
          disabled={loading}
        >
          {loading ? 'Running...' : 'Run Audit'}
        </Button>
        <Button
          variant="contained"
          size="large"
          color="success"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
          onClick={runAutomatedAudit}
          disabled={loading}
        >
          {loading ? 'Running...' : 'Run Automated Audit'}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Automation Summary */}
      {automationSummary && (
        <Paper sx={{ p: 3, mb: 4, bgcolor: '#E3F2FD' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" gutterBottom color="primary">
              🤖 Automation Summary
            </Typography>
            <Typography variant="body2" color="textSecondary">
              The following actions were performed automatically
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>✅ Sales Added:</strong> {automationSummary.salesAdded}
            </Typography>
            <Typography variant="body1">
              <strong>📧 Emails Fixed:</strong> {automationSummary.emailsFixed}
            </Typography>
            <Typography variant="body1">
              <strong>⚠️ Coupon Mismatches:</strong> {automationSummary.couponMismatches}
            </Typography>
            <Typography variant="body1">
              <strong>🔔 Notifications Sent:</strong> {automationSummary.salesAdded - automationSummary.notificationsFailed}
            </Typography>
            {automationSummary.notificationsFailed > 0 && (
              <Typography variant="body1" color="warning.main">
                <strong>⚠️ Notifications Failed:</strong> {automationSummary.notificationsFailed}
              </Typography>
            )}
          </Box>

          {automationSummary.failedNotifications && automationSummary.failedNotifications.length > 0 && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="warning.dark" gutterBottom>
                <strong>Failed Notifications:</strong>
              </Typography>
              {automationSummary.failedNotifications.map((item: any, index: number) => (
                <Typography key={index} variant="caption" display="block">
                  • {item.username}: {item.customerName} - {item.reason}
                </Typography>
              ))}
            </>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              📱 Telegram notifications have been sent to sellers and management team
            </Typography>
          </Alert>
        </Paper>
      )}

      {/* Audit Results */}
      {auditResults && (
        <>
          {/* Sheet Update Status */}
          {auditResults.sheetUpdate && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>✓ Attribution sheet updated:</strong> {auditResults.sheetUpdate.rowsUpdated} rows 
                ({auditResults.sheetUpdate.cellsUpdated} cells) synced from source sheet
              </Typography>
            </Alert>
          )}

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Attribution IDs
                      </Typography>
                      <Typography variant="h4">
                        {auditResults?.summary?.totalAttributionRecords || 0}
                      </Typography>
                    </Box>
                    <AssignmentIcon color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Match Rate
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {auditResults?.summary?.foundInBoth || 0}
                      </Typography>
                    </Box>
                    <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Missing from Sales Tracker
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        {auditResults?.summary?.missingFromSalesTracker || 0}
                      </Typography>
                    </Box>
                    <ErrorIcon color="error" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Email Mismatches
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {auditResults?.summary?.needsEmailUpdate || 0}
                      </Typography>
                    </Box>
                    <InfoIcon color="warning" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box>
                    <Typography color="textSecondary" variant="body2" gutterBottom>
                      Audit Date Range
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(auditResults?.dateRange?.from)}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      to {formatDate(auditResults?.dateRange?.to)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Run at: {formatDate(auditResults?.auditDate)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Email Mismatches - Name match but wrong email */}
          {auditResults?.needsEmailUpdate && auditResults.needsEmailUpdate.length > 0 && (
            <Paper sx={{ p: 3, mb: 4, bgcolor: '#FFF9E6' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" gutterBottom color="warning.dark">
                  Email Mismatches (Name Found, Email Incorrect)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  These customers are in both sheets with matching names, but the email in Sales Tracker is incorrect
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Customer Name</strong></TableCell>
                      <TableCell><strong>Correct Email (Attribution)</strong></TableCell>
                      <TableCell><strong>Incorrect Email (Sales Tracker)</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Sales Rep</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditResults?.needsEmailUpdate?.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <strong>{`${record.firstName || ''} ${record.lastName || ''}`.trim()}</strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.correctEmail}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.incorrectEmail || 'N/A'}
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{record.phone || 'N/A'}</TableCell>
                        <TableCell>{record.salesRep || 'N/A'}</TableCell>
                        <TableCell>{formatDate(record.buyerDate)}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={() => handleUpdateEmail(record)}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Fix Email
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Missing from Sales Tracker - VALID COUPONS (Can Auto-Add) */}
          {auditResults?.missingWithValidCoupon && auditResults.missingWithValidCoupon.length > 0 && (
            <Paper sx={{ p: 3, mb: 4, bgcolor: '#E8F5E9' }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" gutterBottom color="success.dark">
                    Ready to Auto-Add ({auditResults.missingWithValidCoupon.length} sales)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    These sales have valid coupon codes and can be automatically added to the Sales Tracker
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => handleBulkAdd(auditResults.missingWithValidCoupon!)}
                  disabled={loading}
                >
                  Auto-Add All {auditResults.missingWithValidCoupon.length} Sales
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Customer Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Sales Rep</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell><strong>Discount</strong></TableCell>
                      <TableCell><strong>Coupon</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditResults?.missingWithValidCoupon?.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <strong>{`${record.firstName || ''} ${record.lastName || ''}`.trim()}</strong>
                        </TableCell>
                        <TableCell>{record.email || 'N/A'}</TableCell>
                        <TableCell>{record.phone || 'N/A'}</TableCell>
                        <TableCell>{record.salesRep || 'N/A'}</TableCell>
                        <TableCell>{formatDate(record.buyerDate)}</TableCell>
                        <TableCell>
                          <Chip label={record.product} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={record.discount} color="success" size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={record.coupon} color="primary" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Missing from Sales Tracker - INVALID COUPONS (Cannot Auto-Add) */}
          {auditResults?.missingWithInvalidCoupon && auditResults.missingWithInvalidCoupon.length > 0 && (
            <Paper sx={{ p: 3, mb: 4, bgcolor: '#FFEBEE' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" gutterBottom color="error.dark">
                  Unmatched Coupons ({auditResults.missingWithInvalidCoupon.length} sales)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  These sales have coupon codes that don't match any known product/discount mapping and require manual review
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Customer Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Sales Rep</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Coupon</strong></TableCell>
                      <TableCell><strong>Sale Amount</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditResults?.missingWithInvalidCoupon?.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <strong>{`${record.firstName || ''} ${record.lastName || ''}`.trim()}</strong>
                        </TableCell>
                        <TableCell>{record.email || 'N/A'}</TableCell>
                        <TableCell>{record.phone || 'N/A'}</TableCell>
                        <TableCell>{record.salesRep || 'N/A'}</TableCell>
                        <TableCell>{formatDate(record.buyerDate)}</TableCell>
                        <TableCell>
                          <Chip label={record.coupon || 'N/A'} color="error" size="small" />
                        </TableCell>
                        <TableCell>{formatCurrency(record.saleAmount)}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleAddToSalesTracker(record)}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            Manual Add
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* All Missing Combined - For Reference */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                All Missing from Sales Tracker (Combined View)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Complete list of all sales missing from Sales Tracker
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {auditResults?.missingFromSalesTracker?.length === 0 ? (
              <Alert severity="success" icon={<CheckIcon />}>
                All sales in the Attribution Sheet are present in the Sales Tracker. No discrepancies found!
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>First Name</strong></TableCell>
                      <TableCell><strong>Last Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Sales Rep</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Sale Amount</strong></TableCell>
                      <TableCell><strong>Coupon</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditResults?.missingFromSalesTracker?.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{record.firstName || 'N/A'}</TableCell>
                        <TableCell>{record.lastName || 'N/A'}</TableCell>
                        <TableCell>{record.email || 'N/A'}</TableCell>
                        <TableCell>{record.phone || 'N/A'}</TableCell>
                        <TableCell>{record.salesRep || 'N/A'}</TableCell>
                        <TableCell>{formatDate(record.buyerDate)}</TableCell>
                        <TableCell>{formatCurrency(record.saleAmount)}</TableCell>
                        <TableCell>{record.coupon || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={record.reason || 'Unknown'}
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddToSalesTracker(record)}
                            sx={{ 
                              bgcolor: '#61ABBC', 
                              '&:hover': { bgcolor: '#4B88A2' },
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Add to Tracker
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Found in Both - For Reference */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Matched Sales (Found in Both)
              </Typography>
              <Typography variant="body2" color="textSecondary">
                These sales appear in both the Attribution Sheet and Sales Tracker
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {auditResults?.foundInBoth?.length === 0 ? (
              <Alert severity="info">
                No matched sales found.
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Attribution ID</strong></TableCell>
                        <TableCell><strong>Sales Rep</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Sale Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditResults?.foundInBoth?.slice(0, 50).map((record, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{record.email || 'N/A'}</TableCell>
                          <TableCell>{record.attributionId || 'N/A'}</TableCell>
                          <TableCell>{record.salesRep || 'N/A'}</TableCell>
                          <TableCell>{formatDate(record.buyerDate)}</TableCell>
                          <TableCell>{formatCurrency(record.saleAmount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {auditResults?.foundInBoth && auditResults.foundInBoth.length > 50 && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Showing first 50 of {auditResults.foundInBoth.length} matched sales
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Add to Sales Tracker Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1, color: '#61ABBC' }} />
            Add Sale to Sales Tracker
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Date */}
            <Grid item xs={12}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            {/* Customer Name */}
            <Grid item xs={12}>
              <TextField
                label="Customer Name"
                fullWidth
                value={formData.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                required
              />
            </Grid>

            {/* Contact Number */}
            <Grid item xs={12}>
              <TextField
                label="Contact Number"
                fullWidth
                value={formData.contactNumber}
                onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                placeholder="(555) 123-4567"
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="customer@example.com"
                required
              />
            </Grid>

            {/* Product */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Product</InputLabel>
                <Select
                  value={formData.product}
                  label="Product"
                  onChange={(e: SelectChangeEvent<string>) => handleInputChange('product', e.target.value)}
                >
                  {products.map((product) => (
                    <MenuItem key={product} value={product}>
                      {product}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Discount */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Discount</InputLabel>
                <Select
                  value={formData.discount}
                  label="Discount"
                  onChange={(e: SelectChangeEvent<string>) => handleInputChange('discount', e.target.value)}
                >
                  {discounts.map((discount) => (
                    <MenuItem key={discount} value={discount}>
                      {discount}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}

          {formSuccess && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mt: 2 }}>
              {formSuccess}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#61ABBC', '&:hover': { bgcolor: '#4B88A2' } }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Add Sale'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SalesAuditTool;
